import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Carousel,
  CarouselContent,
  CarouselPrevious,
  CarouselNext,
  CarouselItem,
} from "@altimateai/lego";
import { IconBook2, IconX } from "@tabler/icons-react";
import { useChatContext } from "../../ChatProvider";
import { CodeBlock, Stack } from "@altimateai/lego";
import { ContextOption, DetectedEntity, LoadingState } from "../../types";
import { EntityItem } from "../entityItem";
import {
  usePromptInputAttachments,
  Attachment,
  AttachmentPreview,
  AttachmentRemove,
  AttachmentsContext,
} from "@altimateai/lego";
import { updateContext } from "../../store/chatSlice";

interface SelectedContextsProps {
  contextOptions?: ContextOption[];
  onRemoveContext: (id: string, key?: string) => void;
}

export const SelectedContexts: React.FC<SelectedContextsProps> = ({
  contextOptions,
  onRemoveContext,
}) => {
  const { currentSessionId, sessions, loadingState, dispatch } =
    useChatContext();
  const currentContext = useMemo(
    () => (currentSessionId ? sessions[currentSessionId]?.context : {}),
    [currentSessionId, sessions],
  );
  const [selectedContextKey] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Build selectedOptions from context for SelectedContexts component
  const getSelectedOptionsFromContext = () => {
    const options: { [x: string]: string[] } = {};
    if (contextOptions && currentContext) {
      contextOptions.forEach((contextOption) => {
        if (
          contextOption.type === "submenu" &&
          currentContext[contextOption.key]
        ) {
          options[contextOption.key] = currentContext[
            contextOption.key
          ] as string[];
        }
      });
    }
    return options;
  };

  const selectedOptions = getSelectedOptionsFromContext();
  // Get attachments from prompt input
  const attachments = usePromptInputAttachments();

  // Get entities from context
  const entities = (currentContext?.entities as DetectedEntity[]) || [];

  // Check if we have any items to display in carousel
  const hasItems =
    attachments.files.length > 0 ||
    Object.values(selectedOptions).some((options) => options.length > 0) ||
    entities.length > 0;

  // Also clear when loading state changes to LOADING (when message is being sent)
  useEffect(() => {
    if (loadingState === LoadingState.LOADING && currentSessionId) {
      attachments.clear();
      const options: { [x: string]: string[] } = {};
      if (contextOptions && currentContext) {
        contextOptions.forEach((contextOption) => {
          if (contextOption.type === "submenu") {
            options[contextOption.key] = [];
          }
        });
      }
      dispatch(
        updateContext({
          sessionId: currentSessionId,
          context: options,
        }),
      );
    }
    // dont add attachments, dipatch and currentContext to dependencies to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingState, contextOptions, currentSessionId]);

  if (!hasItems) {
    return null;
  }

  return (
    <>
      {/* Display all contexts: attachments, knowledge base items, and entities */}
      <div
        className="al-w-full al-px-10 al-pb-2"
        role="region"
        aria-label="Selected contexts"
      >
        <Carousel
          orientation={"horizontal"}
          className="al-w-full"
          opts={{
            align: "start",
            slidesToScroll: "auto",
          }}
        >
          <CarouselContent>
            {/* Display attachments first */}
            <AttachmentsContext.Provider value={{ variant: "inline" }}>
              {attachments.files.map((attachment) => (
                <CarouselItem key={attachment.id}>
                  <Attachment
                    data={attachment}
                    onRemove={() => attachments.remove(attachment.id)}
                    className="al-bg-blue-50 al-px-3 al-py-1 al-rounded-md al-gap-2 al-w-64"
                  >
                    <AttachmentPreview />
                    <AttachmentRemove
                      label={`Remove ${attachment.filename} file`}
                    />
                  </Attachment>
                </CarouselItem>
              ))}
            </AttachmentsContext.Provider>

            {/* Display selected options (Knowledge base items) */}
            {Object.entries(selectedOptions).map(([key, options]) =>
              options.map((optionId) => {
                // Find the option label in the current context
                const contextOption = contextOptions?.find(
                  (opt) => opt.key === key,
                );
                const option =
                  contextOption?.type === "submenu"
                    ? contextOption.options?.find(
                        (opt: { id: string; label: string }) =>
                          opt.id === optionId,
                      )
                    : null;
                if (!option) return null;
                return (
                  <CarouselItem key={optionId}>
                    <Stack
                      direction="row"
                      align="center"
                      className="al-bg-blue-50 al-px-3 al-py-1 al-rounded-md al-gap-2 al-w-64"
                      role="listitem"
                    >
                      <IconBook2 className="al-h-6 al-w-6 al-flex-shrink-0 al-text-brand" />
                      <span className="al-text-sm al-truncate al-flex-1">
                        {option.label}
                      </span>
                      <Button
                        variant="ghost"
                        size="xs"
                        className="al-p-0 al-h-4 al-w-4 al-ml-1 al-flex-shrink-0 al-text-gray-500"
                        onClick={() => onRemoveContext(optionId, key)}
                        aria-label={`Remove ${option.label} context`}
                      >
                        <IconX aria-hidden={"true"} />
                      </Button>
                    </Stack>
                  </CarouselItem>
                );
              }),
            )}

            {/* Display detected entities */}
            {entities.map((entity) => (
              <CarouselItem key={entity.name}>
                <EntityItem key={entity.name} entity={entity} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious
            className={"al-bg-white"}
            size={"xs"}
            type="button"
            title="Show previous"
          />
          <CarouselNext
            className={"al-bg-white"}
            size={"xs"}
            type="button"
            title="Show next"
          />
        </Carousel>
      </div>

      {/* Context Value Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          className="al-max-w-2xl al-max-h-[50vh] al-overflow-y-hidden"
          aria-describedby="context-content-description"
        >
          <DialogHeader>
            <DialogTitle>
              {selectedContextKey && `Context: ${selectedContextKey}`}
            </DialogTitle>
            <DialogDescription id="context-content-description">
              Content of the selected context file
            </DialogDescription>
          </DialogHeader>
          <div
            className="al-overflow-x-auto"
            style={{ maxHeight: "40%", minHeight: "85px" }}
          >
            {selectedContextKey && (
              <CodeBlock
                language="json"
                code={currentContext?.[selectedContextKey] as string}
                theme="dark"
                editorTheme="tomorrow"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
